import axios, { AxiosInstance } from 'axios';

// CRITICAL: Base URL should NOT include /api/v1 - endpoints include it
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.cyrenza.com';

/**
 * Generate a unique request ID for tracking
 */
const generateRequestId = (): string => {
  return `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

class AdminApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // 30 seconds
      
      // CRITICAL: withCredentials MUST be true for CORS with credentials
      withCredentials: true,
      
      // Standard headers - don't add Authorization here (use interceptor)
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // ==================== REQUEST INTERCEPTOR ====================
    this.client.interceptors.request.use(
      (config) => {
        // Add authentication token
        const token = localStorage.getItem('admin_access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add unique request ID for tracking
        config.headers['X-Request-ID'] = generateRequestId();
        
        // Add client identification headers
        config.headers['X-Client-Version'] = '2.1.0';
        config.headers['X-Client-Type'] = 'admin-panel';

        // Log requests in development
        if (import.meta.env.DEV) {
          console.log(`[ADMIN API] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, {
            headers: config.headers,
            data: config.data,
            params: config.params,
          });
        }

        return config;
      },
      (error) => {
        console.error('[ADMIN API] Request Error:', error);
        return Promise.reject(error);
      }
    );

    // ==================== RESPONSE INTERCEPTOR ====================
    this.client.interceptors.response.use(
      (response) => {
        // Log successful responses in dev mode
        if (import.meta.env.DEV) {
          console.log(`[ADMIN API] Response ${response.status}:`, {
            url: `${response.config.baseURL}${response.config.url}`,
            method: response.config.method?.toUpperCase(),
            data: response.data,
            requestId: response.config.headers['X-Request-ID'],
          });
        }
        
        // ✅ CRITICAL FIX: Unwrap standardized backend response format
        // Backend returns: { status: "success", data: { data: { ...actual data } } }
        // We need to extract the nested data object
        if (response.data && response.data.data && response.data.data.data) {
          console.log('[ADMIN API] Unwrapping nested response.data.data.data');
          return { ...response, data: response.data.data.data };
        }
        // Some endpoints return: { status: "success", data: { ...actual data } }
        else if (response.data && response.data.data) {
          console.log('[ADMIN API] Unwrapping nested response.data.data');
          return { ...response, data: response.data.data };
        }
        
        return response;
      },
      (error) => {
        // Enhanced error handling
        if (error.response) {
          // Server responded with error status
          const { status, data } = error.response;

          console.error(`[ADMIN API] Error ${status}:`, {
            url: `${error.config?.baseURL}${error.config?.url}`,
            method: error.config?.method?.toUpperCase(),
            status,
            data,
            requestId: error.config?.headers?.['X-Request-ID'],
          });

          // Handle specific error cases
          switch (status) {
            case 401:
              // Unauthorized - Clear token and redirect to login
              console.error('[ADMIN API] 401 Unauthorized - Redirecting to login');
              localStorage.removeItem('admin_access_token');
              localStorage.removeItem('admin_refresh_token');
              localStorage.removeItem('is_admin');
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
              break;

            case 403:
              // Forbidden - Not an admin
              console.error('[ADMIN API] 403 Forbidden - Admin access required');
              break;

            case 429:
              // Rate limited
              console.error('[ADMIN API] 429 Rate Limited - Please try again later');
              break;

            case 500:
            case 502:
            case 503:
            case 504:
              // Server errors
              console.error(`[ADMIN API] ${status} Server Error - Backend issue`);
              break;
          }
        } else if (error.request) {
          // Request made but no response received (network error, CORS issue, timeout)
          console.error('[ADMIN API] No Response:', {
            url: `${error.config?.baseURL}${error.config?.url}`,
            method: error.config?.method?.toUpperCase(),
            error: error.message,
            code: error.code,
          });
          
          // Check for common CORS/network issues
          if (error.code === 'ERR_NETWORK') {
            console.error('[ADMIN API] Network Error - Check CORS configuration or internet connection');
          } else if (error.code === 'ECONNABORTED') {
            console.error('[ADMIN API] Timeout - Request took longer than 30 seconds');
          }
        } else {
          // Error setting up request
          console.error('[ADMIN API] Request Setup Error:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  // ==================== AUTHENTICATION ====================
  
  /**
   * Admin login
   * POST /api/v1/auth/login
   * 
   * IMPORTANT: Returns access_token and user data
   * Backend validates admin privileges (is_superuser)
   */
  async login(email: string, password: string) {
    try {
      const response = await this.client.post('/api/v1/auth/login', { 
        email, 
        password 
      });
      
      // Backend returns:
      // {
      //   access_token: "eyJ...",
      //   refresh_token: "eyJ...",
      //   token_type: "bearer",
      //   user: {
      //     id: "...",
      //     email: "...",
      //     is_superuser: true/false,
      //     ...
      //   }
      // }
      
      return response.data;
    } catch (error: any) {
      console.error('[ADMIN API] Login failed:', {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      throw error;
    }
  }

  /**
   * Refresh access token
   * POST /api/v1/auth/refresh
   */
  async refreshToken(refreshToken: string) {
    const response = await this.client.post('/api/v1/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  }

  /**
   * Get current admin user
   * GET /api/v1/auth/me
   */
  async getCurrentUser() {
    const response = await this.client.get('/api/v1/auth/me');
    return response.data;
  }

  // ==================== USER MANAGEMENT ====================
  
  /**
   * Create new customer account (Sales-led provisioning)
   * POST /api/v1/auth/admin/create-user
   */
  async createUser(userData: CreateUserRequest) {
    const response = await this.client.post('/auth/admin/create-user', userData);
    return response.data;
  }

  /**
   * List all users with pagination and filters
   * GET /api/v1/users
   */
  async listUsers(
    page: number = 1,
    limit: number = 50,
    filters?: {
      search?: string;
      role?: string;
      is_active?: boolean;
      tenant_id?: string;
    }
  ) {
    const params: any = { page, limit };
    if (filters?.search) params.search = filters.search;
    if (filters?.role) params.role = filters.role;
    if (filters?.is_active !== undefined) params.is_active = filters.is_active;
    if (filters?.tenant_id) params.tenant_id = filters.tenant_id;
    
    const response = await this.client.get('/users', { params });
    return response.data;
  }

  /**
   * Get detailed user information
   * GET /api/v1/users/{user_id}
   */
  async getUser(userId: string) {
    const response = await this.client.get(`/users/${userId}`);
    return response.data;
  }

  /**
   * Update user details
   * PUT /api/v1/users/{user_id}
   */
  async updateUser(userId: string, updates: Partial<UpdateUserRequest>) {
    const response = await this.client.put(`/users/${userId}`, updates);
    return response.data;
  }

  /**
   * Soft-delete user account
   * DELETE /api/v1/users/{user_id}
   */
  async deleteUser(userId: string) {
    const response = await this.client.delete(`/users/${userId}`);
    return response.data;
  }

  /**
   * Admin reset user password
   * POST /api/v1/users/{user_id}/reset-password
   */
  async resetUserPassword(userId: string, newPassword: string) {
    const response = await this.client.post(`/users/${userId}/reset-password`, {
      new_password: newPassword,
    });
    return response.data;
  }

  // ==================== TENANT MANAGEMENT ====================
  
  /**
   * Create enterprise tenant with isolated database schema
   * POST /api/v1/tenants
   */
  async createTenant(tenantData: CreateTenantRequest) {
    const response = await this.client.post('/tenants', tenantData);
    return response.data;
  }

  /**
   * List all enterprise tenants
   * GET /api/v1/tenants
   */
  async listTenants(page: number = 1, limit: number = 50, status?: string) {
    const params: any = { page, limit };
    if (status) params.status = status;
    const response = await this.client.get('/tenants', { params });
    return response.data;
  }

  /**
   * Get tenant details
   * GET /api/v1/tenants/{tenant_id}
   */
  async getTenant(tenantId: string) {
    const response = await this.client.get(`/tenants/${tenantId}`);
    return response.data;
  }

  /**
   * Validate tenant data isolation (security test)
   * POST /api/v1/tenants/{tenant_id}/validate
   */
  async validateTenantIsolation(tenantId: string) {
    const response = await this.client.post(`/tenants/${tenantId}/validate`);
    return response.data;
  }

  /**
   * Create encrypted backup of tenant data
   * POST /api/v1/tenants/{tenant_id}/backup
   */
  async backupTenant(tenantId: string) {
    const response = await this.client.post(`/tenants/${tenantId}/backup`);
    return response.data;
  }

  /**
   * Delete tenant and ALL data (PERMANENT)
   * DELETE /api/v1/tenants/{tenant_id}
   */
  async deleteTenant(tenantId: string, reason: string) {
    const response = await this.client.delete(`/tenants/${tenantId}`, {
      data: {
        confirm_tenant_id: tenantId,
        reason,
      },
    });
    return response.data;
  }

  // ==================== ANALYTICS ====================
  
  /**
   * Dashboard overview stats
   * GET /api/v1/admin/analytics/overview
   */
  async getOverviewStats() {
    const response = await this.client.get('/admin/analytics/overview');
    return response.data;
  }

  /**
   * Detailed usage analytics
   * GET /api/v1/admin/analytics/usage
   */
  async getUsageAnalytics(period: number = 30, tenant_id?: string) {
    const params: any = { period };
    if (tenant_id) params.tenant_id = tenant_id;
    const response = await this.client.get('/admin/analytics/usage', { params });
    return response.data;
  }

  /**
   * Recent admin/system activity
   * GET /api/v1/admin/activity-log
   */
  async getActivityFeed(limit: number = 50, action?: string) {
    const params: any = { limit };
    if (action) params.action = action;
    const response = await this.client.get('/admin/activity-log', { params });
    return response.data;
  }
}

// ==================== TYPE DEFINITIONS ====================

export interface CreateUserRequest {
  email: string;
  password: string;
  full_name: string;
  workspace_name: string;
  company_name: string;
  role: 'admin' | 'user';
  subscription_tier: 'free' | 'business' | 'professional' | 'enterprise';
  monthly_token_limit: number;
}

export interface UpdateUserRequest {
  full_name?: string;
  organization?: string;
  subscription_tier?: string;
  monthly_token_limit?: number;
  is_active?: boolean;
}

export interface CreateTenantRequest {
  tenant_id: string;
  tenant_name: string;
  admin_email: string;
  plan: 'enterprise' | 'professional';
}

export const adminApi = new AdminApiClient();
